import React, { PropTypes } from 'react';
import { Link } from 'react-router';

import { eventFullPropTypeShape } from '../../proptypes';
import HeartButton from '../../containers/HeartButton';
import StarRating from '../StarRating';
import Tag from '../Tag';

import { srOnly } from '../../base-styles/mixins.module.css';
import {
  card,
  details,
  gradient,
  header,
  heart,
  image as imageStyle,
  imageContainer,
  info,
  link,
  location as locationStyle,
  priceRange,
  rating,
  redTag,
  saveEventContainer,
  strapline,
  tag,
  tagContainer,
  title as titleStyle,
} from './styles.module.css';

/**
 * Event card presentational component.
 */
const EventCard = ({
  className,
  children,
  description,
  id,
  images,
  linkProps,
  location,
  notifications,
  organiser,
  permalink,
  races,
  reviews,
  saves,
  source,
  startDate,
  startDateFormatted,
  title,
  views,
}) => (
  <article className={[card, className].join(' ')}>
    <header className={header}>
      <div className={tagContainer}>
        {races &&
          races.bookingPartner && <Tag className={redTag} text={races.bookingPartner.value} />}
        {notifications && notifications.map(({ type, value }) =>
          <Tag key={type} className={tag} text={value} red={type < 3} />)}
      </div>
      <div className={imageContainer}>
        <div style={{ backgroundImage: `url(${images.hero.large})` }} className={imageStyle} alt="" />
      </div>
      <div className={gradient} />
      <div className={info}>
        {reviews &&

          <div className={rating}>
            <StarRating value={reviews.ratings.overall} small />
          </div>}
        <div className={srOnly} dangerouslySetInnerHTML={{ __html: description }} />
        <h2 className={titleStyle}>{title}</h2>
        <div className={locationStyle}>
          <span className={details} >
            {startDate &&
            <time dateTime={startDate}> {startDateFormatted}</time>}
            {(location && location.address) &&
            <span>{startDate && ' • '}
              <span>{location.address.join(', ')}</span>
            </span>}
          </span>
        </div>
      </div>
    </header>
    <div className={strapline}>
      {races && races.tags && (
        <div style={{ display: 'none' }}>
          {// eslint-disable-next-line
          races.tags.map((t, i) => <Link key={i} to={t.link}>{t.value}</Link>)}
        </div>
      )}
      <span>
        {(races && races.races) && (races.races.length > 1 ? `${races.races.length} races` : `${races.races.length} race`)}
        {(!races || !races.races) && 'More Information'}
      </span>
      {id !== 84206 && races && races.pricesFromFormatted &&
        <div className={priceRange}>
          {/* eslint-disable no-nested-ternary */
            races.standardTickets ? (
              <div>
                {races.pricesFromFormatted}
                {races.pricesToFormatted && <span>–{races.pricesToFormatted}</span>}
              </div>
            ) :
            races.charityTickets
              ? 'Charity Tickets'
              : races.ballotTickets ? 'Ballot Tickets' : null
            /* eslint-enable */
            }
        </div>}
    </div>
    <Link
      to={{
        pathname: permalink,
        hash: source ? `#source=${source}` : undefined,
      }}
      className={link}
      {...linkProps}
    >
      <span className={srOnly}>{title}</span>
    </Link>
    <div className={saveEventContainer}>
      <HeartButton
        id={id}
        count={saves ? saves.total : 0}
        bookingPartner={races && races.bookingPartner}
        views={views}
        className={heart}
        trackingObject={{
          id,
          title,
          organiser,
          startDate,
          tags: notifications,
        }}
      ><div className="heart" aria-hidden="true" />
      </HeartButton>
    </div>
    {children}
  </article>
);

EventCard.propTypes = {
  /**
   * String class names to append to component styles.
   */
  className: PropTypes.string,
  /**
   * Children contents of button.
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  /**
   * Numeric ID of the event.
   */
  id: eventFullPropTypeShape.id, // eslint-disable-line react/require-default-props
  /**
   * Object of images associated with the event.
   */
  images: eventFullPropTypeShape.images,
  /**
   * Additional properties to add to the permalink.
   */
  linkProps: PropTypes.shape(),
  /**
   * Object of location data.
   */
  location: eventFullPropTypeShape.location,
  /**
   * Array of notification objects.
   */
  notifications: eventFullPropTypeShape.notifications,
  /**
   * Event organiser object.
   */
  organiser: eventFullPropTypeShape.organiser,
  /**
   * String permalink to the event page.
   */
  permalink: eventFullPropTypeShape.permalink, // eslint-disable-line react/require-default-props
  /**
   * Object of race data.
   */
  races: eventFullPropTypeShape.races,
  /**
   * Object of reviews data.
   */
  reviews: eventFullPropTypeShape.reviews,
  /**
   * Object of event saves data.
   */
  saves: eventFullPropTypeShape.saves,
  /**
   * Optional source location of card.
   */
  source: PropTypes.string,
  /**
   * ISO string of event start date.
   */
  startDate: eventFullPropTypeShape.startDate, // eslint-disable-line react/require-default-props
  /**
   * Formatted string of race start date.
   */
  startDateFormatted: eventFullPropTypeShape.startDateFormatted, // eslint-disable-line max-len, react/require-default-props
  /**
   * String title of the event.
   */
  title: eventFullPropTypeShape.title, // eslint-disable-line react/require-default-props
  /**
   * Stringdescriptiontitle of the event.
   */
  description: eventFullPropTypeShape.description,
  /**
   * String excerpt of the event.
   */
  excerpt: eventFullPropTypeShape.excerpt, // eslint-disable-line react/require-default-props
  /**
   * Number of event views.
   */
  views: eventFullPropTypeShape.views,
};

EventCard.defaultProps = {
  children: [],
  className: '',
  description: undefined,
  excerpt: '',
  images: undefined,
  linkProps: {},
  location: undefined,
  notifications: undefined,
  organiser: null,
  races: undefined,
  reviews: undefined,
  saves: undefined,
  source: undefined,
  views: undefined,
};

export default EventCard;



// WEBPACK FOOTER //
// ./app/src/components/EventCard/index.jsx